import { Component, Inject, InjectionToken, Injector, OnInit, PLATFORM_ID, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { first } from "rxjs";
import { ProfileService } from "src/services/profile/profile.service";
import { CBox_AdminGetProcedureDataResponse, CBox_AdminGetProcedureListData, CBox_AdminGetProcedureListDataItem } from "@server/services/cbox/public/api/v1/resources/internal/procedure/types";
import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { handlePublicApiError } from "src/services/api/api.service";
import { CBox_PublicSuccessResponse } from "@server/services/cbox/public/api/v1/resources/common/request_base/types";
import { ButtonModule } from "primeng/button";
import { TableModule, TableRowCollapseEvent, TableRowExpandEvent } from "primeng/table";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { ProgressBarModule } from "primeng/progressbar";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";
import { DropdownModule } from "primeng/dropdown";
import { SkeletonModule } from "primeng/skeleton";
import { CalendarModule } from "primeng/calendar";
import { MatDialog } from "@angular/material/dialog";
import { CBoxProfilePickupRequestInfoComponent } from "app/profile/requests/pickup/dialogs/info/cbox-profile-pickup-request-info.component";
import { PaginatedTableWithFilters } from "app/shared/paginated-table-with-filters";

@Component({
  selector: "app-cbox-profile-admin-locker-procedures-list",
  templateUrl: "./cbox-profile-admin-locker-procedures-list.component.html",
  styleUrls: ["./cbox-profile-admin-locker-procedures-list.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    ReactiveFormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ProgressBarModule,
    MultiSelectModule,
    DropdownModule,
    SkeletonModule,
    CalendarModule
  ]
})

export class CBoxProfileAdminLockerProceduresListComponent extends PaginatedTableWithFilters<CBox_AdminGetProcedureListDataItem[], CBox_AdminGetProcedureListData> implements OnInit {

  filtersForm: FormGroup;
  dataFetchUrl = "internal/procedures";
  localStorageFiltersKey = "admin/procedures-list/filters";
  localStorageFiltersToSave = ["pageSize"];
  sessionFiltersKey = "admin/procedures-list/filters";
  sessionFiltersToSave = ["page", "id", "type", "procedureId", "interval"];
  proceduresData = signal<Record<string, CBox_AdminGetProcedureDataResponse>>({});
  fetchingProcedureData = signal<Record<string, true>>({});
  expandedRows = signal<Record<string, boolean>>({});

  oneWeekAgo = new Date(new Date().setDate(new Date().getDate() - 7));

  defaultFilters = signal({
    page: 1,
    pageSize: 100,
    id: [],
    type: [],
    procedureId: "",
    interval: [this.oneWeekAgo, this.today]
  });

  procedureTypes = signal([
    {
      label: "PickUp",
      value: "USER_PICK_UP"
    },
    {
      label: "DropOff",
      value: "USER_DROP_OFF"
    },
    {
      label: "System",
      value: "SYSTEM"
    },
    {
      label: "System update",
      value: "SYSTEM_UPDATE"
    },
    {
      label: "Agent operation",
      value: "AGENT_OPERATION"
    },
    {
      label: "Unknown",
      value: "UNKNOWN"
    },
  ]);

  constructor(
    inj: Injector,
    private profileService: ProfileService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    @Inject(PLATFORM_ID) private platformId: InjectionToken<Object>) {
      super(inj);
      this.oneWeekAgo.setHours(0, 0, 0, 0);
      this.filtersForm = this.fb.group({
        page: [1],
        pageSize: [100],
        id: [[]],
        type: [undefined],
        procedureId: [""],
        interval: [[this.oneWeekAgo, this.today]]
      });
    }

  ngOnInit(): void {
    this.profileService.setTitle("Listă evenimente locker");
  }

  expandRow(procedure: TableRowExpandEvent): void {
    this.expandedRows.update(rows => {
      return {
        ...rows,
        [procedure.data.uniqueProcedureId]: true
      };
    })
    this.fetchProcedureData(procedure.data);
  }

  collapseRow(event: TableRowCollapseEvent): void {
    this.proceduresData.update(proceduresData => {
      delete proceduresData[event.data.uniqueProcedureId];
      return proceduresData;
    });
  }

  openOrderInfo(type: "PICK_UP" | "DROP_OFF", responseId: string): void {
    switch (type) {
      case "PICK_UP":
        this.openPickUpRequestInfo(responseId);
        break;

      default:
        console.warn("Unknown procedure type", type);
        break;
    }
  }

  private openPickUpRequestInfo(responseId: string) {
    const dialog = this.dialog.open(CBoxProfilePickupRequestInfoComponent, {
      data: {
        responseId
      },
      minWidth: "min(1200px, 100%)",
      autoFocus: false
    });

    dialog.afterClosed().pipe(first()).subscribe(() => {
      this.loading.set(true);
      this.fetchData();
    });
  }

  async init(): Promise<void> {}

  protected override onFiltersChange(): void {
    if (this.areBothDatesSelected()) {
      this.loading.set(true);
      this.dataFetchTrigger.next();
    }
  }

  private fetchProcedureData(procedure: CBox_AdminGetProcedureListDataItem): void {
    this.fetchingProcedureData.update(fetchingProcedureData => {
      return {
        ...fetchingProcedureData,
        [procedure.uniqueProcedureId]: true
      };
    });
    this.api.get<CBox_PublicSuccessResponse<CBox_AdminGetProcedureDataResponse>>("backend/internal/procedures/" + procedure.uniqueProcedureId).subscribe((response) => {
      this.proceduresData.update(proceduresData => {
        return {
          ...proceduresData,
          [procedure.uniqueProcedureId]: response.data
        };
      });
      this.fetchingProcedureData.update(fetchingProcedureData => {
        delete fetchingProcedureData[procedure.uniqueProcedureId];
        return fetchingProcedureData;
      });
    }, (e: HttpErrorResponse) => {
      this.fetchingProcedureData.update(fetchingProcedureData => {
        delete fetchingProcedureData[procedure.uniqueProcedureId];
        return fetchingProcedureData;
      });
      this.expandedRows.set({});
      handlePublicApiError(e, this.toastService);
    });
  }

  getSearchStructure(): CBox_AdminGetProcedureListData {
    let interval = this.filtersForm.value.interval;
    if (typeof interval === "string") {
      interval = interval.split(",");
    }

    return Object.entries({
      "lockerIdentifiers[]": this.filtersForm.value.id,
      uniqueProcedureId: this.filtersForm.value.procedureId?.trim(),
      page: +this.filtersForm.value.page,
      pageSize: +this.filtersForm.value.pageSize,
      "procedureTypes[]": this.filtersForm?.value?.type ? this.filtersForm?.value?.type?.map((type: string) => {
        return type;
      }) : undefined,
      "interval[start]": interval?.[0] ? new Date(interval[0]).toISOString() : this.oneWeekAgo.toISOString(),
      "interval[end]": interval?.[1] ? new Date(interval[1]).toISOString() : this.today.toISOString()
    })
    .filter(([_, value]) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value)
    )
    .reduce((acc, [key, value]) => {
      acc[key as keyof CBox_AdminGetProcedureListData] = value;
      return acc;
    }, {} as any);
  }
}